import type { TSetUserDisplayNameRequestPayload } from '@cascade/api-client/user';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '#providers/api/index.ts';
import { useAuthentication } from '#providers/authentication/index.ts';
import { UserContext } from './User.context.ts';
import type { TUserContextValue, TUserProviderProps } from './User.types.ts';

export const UserProvider = ({ children }: TUserProviderProps) => {
  const api = useApi();
  const { user, refresh } = useAuthentication();

  const {
    mutateAsync: setDisplayName,
    isPending: isSettingDisplayName,
    error: setDisplayNameError,
  } = useMutation({
    mutationFn: (payload: TSetUserDisplayNameRequestPayload) =>
      api.user.setUserDisplayName(payload),
    onSuccess: () => refresh().catch(() => undefined),
  });

  const value = useMemo<TUserContextValue>(
    () => ({
      user,
      isSettingDisplayName,
      setDisplayNameError: setDisplayNameError ?? undefined,
      setDisplayName,
    }),
    [user, isSettingDisplayName, setDisplayNameError, setDisplayName],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
